import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ModuleFrame, { Chip, KV } from '../../components/ModuleFrame';
import { apiGet, apiPost, apiPut, apiDelete, isBackendConfigured } from '../../utils/backendApi';

// Administration → Models & credits. Reads GET /orgs/:orgId/models
// (api/modelsApi.js) and shows, for org admins:
//   · Credits — the month's allowance + prepaid balance, what is left, and
//     a Buy-credits button (POST /billing/credits/checkout → Stripe; the
//     webhook credits the wallet). Hosted calls stop at $0 (402) until a
//     top-up; the app itself keeps running; your own keys are never gated.
//   · The ladder — chat tiers + image/video ladders. Editable: the org's
//     own table replaces the plane's (PUT /orgs/:orgId/models).
//   · Catalog — every model the org can reach through the gateway, with
//     the price the ledger charges (card + synced provider catalogs).
//   · Your keys — BYO provider endpoints + keys (SSM, never shown back).
//   · Spend — by tier, by model, by app this month.

const TIERS = ['local', 'bulk', 'strong', 'frontier'];
const MEDIA = ['image', 'video'];
const fmtUsd = (u) => (u == null ? '—' : u > 0 && u < 0.01 ? '< $0.01' : `$${Number(u).toFixed(2)}`);
const fmtTokens = (n) => (n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(0)}k` : String(n || 0));
const mono = { fontFamily: 'ui-monospace, monospace', fontSize: 12 };

const ladderToText = (ladder) => Object.fromEntries(TIERS.map((t) => [t, (ladder?.[t] || []).map((c) => `${c.provider}/${c.model}`).join('\n')]));
const textToLadder = (text) => {
  const out = {};
  for (const t of TIERS) {
    const list = String(text[t] || '').split(/[\n,]/).map((s) => s.trim()).filter(Boolean).map((s) => { const i = s.indexOf('/'); return i > 0 ? { provider: s.slice(0, i), model: s.slice(i + 1) } : null; }).filter(Boolean);
    if (list.length) out[t] = list;
  }
  return Object.keys(out).length ? out : null;
};

function priceOf(c) {
  if (c.perImage != null) return `$${c.perImage}/image`;
  if (c.perSecond != null) return `$${c.perSecond}/s`;
  if (c.inPer1M != null || c.outPer1M != null) return `$${c.inPer1M ?? '?'} in · $${c.outPer1M ?? '?'} out /1M`;
  return '—';
}

export default function ModelsPanel({ orgId, isAdmin }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState('');
  const [editing, setEditing] = useState(false);
  const [ladder, setLadder] = useState({});
  const [media, setMedia] = useState({ image: {}, video: {} });
  const [filter, setFilter] = useState('');
  const [kindFilter, setKindFilter] = useState('all');
  const [topUp, setTopUp] = useState('');
  const [keyForm, setKeyForm] = useState({ name: '', base: '', wire: 'openai', value: '' });

  const load = useCallback(async () => {
    if (!isBackendConfigured() || !isAdmin) return;
    try {
      const d = await apiGet(`/orgs/${orgId}/models`);
      setData(d);
      setErr('');
    } catch (e) { setErr(e.message || 'failed to load'); }
  }, [orgId, isAdmin]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!data) return;
    setLadder(ladderToText(data.effective?.tiers));
    setMedia({ image: ladderToText(data.effective?.mediaTiers?.image), video: ladderToText(data.effective?.mediaTiers?.video) });
  }, [data]);

  const catalog = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return (data?.catalog || []).filter((c) => (kindFilter === 'all' || c.kind === kindFilter) && (!q || c.id.toLowerCase().includes(q))).slice(0, 400);
  }, [data, filter, kindFilter]);

  const act = async (what, fn) => {
    setBusy(what); setErr(''); setNotice('');
    try { await fn(); } catch (e) { setErr(e.message || 'failed'); } finally { setBusy(''); }
  };

  if (!isAdmin) return <p className="cs-help">Model routing and credits are visible to org admins.</p>;
  if (err && !data) return <p className="cs-help">{err}</p>;
  if (!data) return <p className="cs-help">Loading…</p>;

  const eff = data.effective || {};
  const usage = data.usage;
  const byTier = usage?.byTier || {};
  const credits = data.credits;
  const pool = credits ? credits.includedUsd + credits.balanceUsd + credits.debitedUsd : 0;
  const pct = credits && pool > 0 ? Math.max(0, Math.min(100, Math.round((credits.remainingUsd / pool) * 100))) : 0;
  const routes = eff.routes || {};
  const routeRows = Object.entries(routes).map(([r, v]) => [r, typeof v === 'string' ? v : Object.entries(v || {}).map(([k, t]) => `${k === '_default' ? 'default' : k} → ${t}`).join(' · ')]);
  const byApp = Object.entries(data.byApp || {});
  const byModel = Object.entries(usage?.byModel || {}).sort((a, b) => (b[1].usd || 0) - (a[1].usd || 0)).slice(0, 12);

  const buy = (usd) => act('buy', async () => {
    const out = await apiPost('/billing/credits/checkout', { orgId, usd });
    if (out?.url) window.location.assign(out.url);
  });

  const saveLadder = () => act('ladder', async () => {
    const mediaTiers = {};
    for (const k of MEDIA) { const l = textToLadder(media[k] || {}); if (l) mediaTiers[k] = l; }
    await apiPut(`/orgs/${orgId}/models`, { providers: data.models?.providers || {}, tiers: textToLadder(ladder) || {}, mediaTiers });
    setEditing(false);
    setNotice('Ladder saved — new calls route on it within a minute.');
    await load();
  });
  const resetLadder = () => act('ladder', async () => {
    await apiPut(`/orgs/${orgId}/models`, { providers: data.models?.providers || {}, tiers: {}, mediaTiers: {} });
    setEditing(false);
    setNotice('Back on nventr-managed tiers.');
    await load();
  });
  const addKey = () => act('key', async () => {
    const name = keyForm.name.trim().toLowerCase();
    if (!name) throw new Error('Name the provider (e.g. openai, anthropic, or your own endpoint name).');
    const providers = { ...(data.models?.providers || {}) };
    if (keyForm.base.trim()) providers[name] = { base: keyForm.base.trim(), keyName: `${name}-api-key`, wire: keyForm.wire };
    if (keyForm.base.trim()) await apiPut(`/orgs/${orgId}/models`, { providers, tiers: data.models?.tiers || {}, mediaTiers: data.models?.mediaTiers || {} });
    if (keyForm.value.trim()) await apiPut(`/orgs/${orgId}/models/keys/${encodeURIComponent(`${name}-api-key`)}`, { value: keyForm.value.trim() });
    setKeyForm({ name: '', base: '', wire: 'openai', value: '' });
    setNotice(`Key for ${name} stored. Calls on it are yours — they never draw credits.`);
    await load();
  });
  const dropKey = (name, keyName) => act('key', async () => {
    await apiDelete(`/orgs/${orgId}/models/keys/${encodeURIComponent(keyName)}`);
    const providers = { ...(data.models?.providers || {}) };
    delete providers[name];
    await apiPut(`/orgs/${orgId}/models`, { providers, tiers: data.models?.tiers || {}, mediaTiers: data.models?.mediaTiers || {} });
    await load();
  });

  return (
    <>
      {(err || notice) && <p className={err ? 'cs-form-error' : 'cs-help'} role={err ? 'alert' : 'status'}>{err || notice}</p>}
      <ModuleFrame.Columns cols={2}>
        <ModuleFrame.Panel
          title="Credits"
          meta={credits ? `${credits.plan} plan · ${data.period}` : data.period}
          actions={credits ? <Chip tone={credits.unlimited ? 'muted' : credits.exhausted ? 'danger' : pct < 20 ? 'warn' : 'ok'}>{credits.unlimited ? 'unlimited' : credits.exhausted ? 'exhausted' : `${pct}% left`}</Chip> : null}
        >
          {credits ? (
            <>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
                <strong style={{ fontSize: 'var(--fs-2xl, 24px)' }} className="u-tabular">{credits.unlimited ? '∞' : fmtUsd(credits.remainingUsd)}</strong>
                <span className="mf-muted">of hosted model credit left this month</span>
              </div>
              {!credits.unlimited && (
                <div style={{ height: 6, borderRadius: 3, background: 'var(--bg-inset)', overflow: 'hidden', marginBottom: 10 }} aria-hidden="true">
                  <div style={{ width: `${pct}%`, height: '100%', background: credits.exhausted ? 'var(--danger)' : pct < 20 ? 'var(--warn)' : 'var(--ok)' }} />
                </div>
              )}
              <KV rows={[
                ['Included with the plan', `${fmtUsd(credits.includedUsd)} / month${credits.allowanceLeftUsd != null ? ` · ${fmtUsd(credits.allowanceLeftUsd)} unused` : ''}`],
                ['Prepaid balance', fmtUsd(credits.balanceUsd)],
                ['Used this month', `${fmtUsd(credits.spentUsd)} on nventr keys${credits.debitedUsd ? ` · ${fmtUsd(credits.debitedUsd)} drawn from prepaid` : ''}`],
                ['When it runs out', data.enforce ? 'Hosted model calls (chat, embeddings, images, video) return 402 until credits are added. The app keeps running; keys you bring are never paused.' : 'Alert only — nothing is blocked on this plane.'],
              ]} />
              {!credits.unlimited && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 12 }}>
                  {(data.topUpOptions || []).map((u) => (
                    <button key={u} type="button" className="cs-btn cs-btn-sm cs-btn-outline" disabled={!!busy} onClick={() => buy(u)}>+ ${u}</button>
                  ))}
                  <input className="cs-input" style={{ width: 96 }} type="number" min={data.minTopUpUsd} max={data.maxTopUpUsd} step="1" placeholder="other" value={topUp} onChange={(e) => setTopUp(e.target.value)} aria-label="Custom amount in USD" />
                  <button type="button" className="cs-btn cs-btn-sm cs-btn-primary" disabled={!!busy || !(Number(topUp) >= data.minTopUpUsd)} onClick={() => buy(Number(topUp))}>{busy === 'buy' ? 'Opening…' : 'Buy credits'}</button>
                </div>
              )}
              {(data.ledger || []).length > 0 && (
                <details style={{ marginTop: 12 }}>
                  <summary className="cs-help" style={{ cursor: 'pointer' }}>Recent movements</summary>
                  <table className="mf-table" style={{ marginTop: 6 }}>
                    <tbody>
                      {data.ledger.map((row) => (
                        <tr key={row.id}>
                          <td className="mf-muted">{new Date(row.createdAt).toLocaleDateString()}</td>
                          <td>{row.kind}{row.period ? ` · ${row.period}` : ''}</td>
                          <td className="u-tabular" style={{ color: row.usd < 0 ? 'var(--muted)' : 'var(--ok)' }}>{row.usd < 0 ? '−' : '+'}{fmtUsd(Math.abs(row.usd))}</td>
                          <td className="mf-muted u-tabular">{row.balanceAfterUsd != null ? `bal ${fmtUsd(row.balanceAfterUsd)}` : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              )}
            </>
          ) : <p className="cs-help">Credits are not tracked for this organization.</p>}
        </ModuleFrame.Panel>

        <ModuleFrame.Panel title="Spend this month" meta={usage ? `${fmtUsd(usage.usd)} · ${fmtTokens(usage.tokens)} tokens${usage.images ? ` · ${usage.images} images` : ''}${usage.seconds ? ` · ${usage.seconds}s video` : ''}` : 'no calls yet'}>
          <table className="mf-table">
            <thead><tr><th>Tier</th><th>Calls</th><th>Tokens</th><th>Share</th><th>Cost</th></tr></thead>
            <tbody>
              {TIERS.map((t) => { const u = byTier[t]; return (
                <tr key={t}><td style={mono}>{t}</td><td>{u?.calls || 0}</td><td>{fmtTokens(u?.tokens || 0)}</td><td>{u ? `${u.sharePct}%` : '—'}</td><td>{u ? (u.calls && !u.usd ? '< $0.01' : fmtUsd(u.usd)) : '—'}</td></tr>
              ); })}
            </tbody>
          </table>
          {byModel.length > 0 && (
            <>
              <p className="cs-help" style={{ margin: '10px 0 4px' }}>By model</p>
              <table className="mf-table"><tbody>
                {byModel.map(([m, u]) => <tr key={m}><td style={mono}>{m}</td><td className="mf-muted">{fmtTokens(u.tokens || 0)}{u.images ? ` · ${u.images} img` : ''}{u.seconds ? ` · ${u.seconds}s` : ''}</td><td>{fmtUsd(u.usd)}</td></tr>)}
              </tbody></table>
            </>
          )}
          {byApp.length > 0 && (
            <>
              <p className="cs-help" style={{ margin: '10px 0 4px' }}>By app (hosted instances on their gateway key + studio calls)</p>
              <table className="mf-table"><tbody>
                {byApp.map(([id, u]) => <tr key={id}><td style={mono}>{id}</td><td className="mf-muted">{fmtTokens(u.tokens || 0)} tok{u.images ? ` · ${u.images} img` : ''}{u.seconds ? ` · ${u.seconds}s` : ''}</td><td>{fmtUsd(u.usd)}</td></tr>)}
              </tbody></table>
            </>
          )}
        </ModuleFrame.Panel>

        <ModuleFrame.Panel
          title="The ladder"
          meta={eff.source === 'org' ? 'your own tier table' : eff.source === 'plane' ? 'nventr-managed tiers' : 'legacy routing — no tiers configured'}
          actions={(
            <span style={{ display: 'inline-flex', gap: 6 }}>
              <Chip tone={eff.source === 'legacy' ? 'warn' : 'ok'}>{eff.source === 'legacy' ? 'tiers off' : 'tiers on'}</Chip>
              {!editing && <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" onClick={() => setEditing(true)}>Edit</button>}
            </span>
          )}
        >
          {editing ? (
            <div style={{ display: 'grid', gap: 8 }}>
              <p className="cs-help" style={{ margin: 0 }}>One <code>provider/model</code> per line, tried in order. Chat tiers first, then the image and video ladders. Empty = skip that tier. Save replaces nventr&rsquo;s table for this organization; Reset returns to it.</p>
              {TIERS.map((t) => (
                <label key={t} style={{ display: 'grid', gap: 2 }}>
                  <span className="cs-help" style={{ margin: 0 }}>chat · {t}</span>
                  <textarea className="cs-input" rows={2} style={mono} value={ladder[t] || ''} onChange={(e) => setLadder({ ...ladder, [t]: e.target.value })} />
                </label>
              ))}
              {MEDIA.map((k) => ['bulk', 'strong', 'frontier'].map((t) => (
                <label key={`${k}.${t}`} style={{ display: 'grid', gap: 2 }}>
                  <span className="cs-help" style={{ margin: 0 }}>{k} · {t}</span>
                  <textarea className="cs-input" rows={1} style={mono} value={media[k]?.[t] || ''} onChange={(e) => setMedia({ ...media, [k]: { ...(media[k] || {}), [t]: e.target.value } })} />
                </label>
              )))}
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="cs-btn cs-btn-sm cs-btn-primary" disabled={busy === 'ladder'} onClick={saveLadder}>{busy === 'ladder' ? 'Saving…' : 'Save ladder'}</button>
                <button type="button" className="cs-btn cs-btn-sm" disabled={busy === 'ladder'} onClick={() => setEditing(false)}>Cancel</button>
                {eff.source === 'org' && <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" disabled={busy === 'ladder'} onClick={resetLadder}>Reset to nventr tiers</button>}
              </div>
            </div>
          ) : (
            <>
              <table className="mf-table">
                <thead><tr><th>Tier</th><th>Candidates, in order</th></tr></thead>
                <tbody>
                  {TIERS.map((t) => { const list = Array.isArray(eff.tiers?.[t]) ? eff.tiers[t] : []; return (
                    <tr key={t}><td><strong style={mono}>{t}</strong></td><td className="mf-muted" style={mono}>{list.length ? list.map((c) => `${c.provider}/${c.model}`).join(', ') : <span className="mf-muted">— none (skipped)</span>}</td></tr>
                  ); })}
                  {MEDIA.map((k) => { const l = eff.mediaTiers?.[k] || {}; const all = ['bulk', 'strong', 'frontier'].flatMap((t) => (l[t] || []).map((c) => `${c.provider}/${c.model}`)); return (
                    <tr key={k}><td><strong style={mono}>{k}</strong></td><td className="mf-muted" style={mono}>{all.length ? all.join(', ') : <span className="mf-muted">— not configured</span>}</td></tr>
                  ); })}
                </tbody>
              </table>
              <KV rows={[
                ['Starting tier', routeRows.length ? routeRows.map(([r, v]) => `${r}: ${v}`).join(' · ') : 'copilot stage defaults (triage/judge bulk · planner/codegen strong)'],
                ['Escalates on', eff.escalation ? `${(eff.escalation.on || []).join(', ')} · up to ${eff.escalation.maxSteps} step${eff.escalation.maxSteps === 1 ? '' : 's'}` : 'off'],
                ['Caps', eff.caps ? [eff.caps.frontierSharePct != null ? `frontier ≤ ${eff.caps.frontierSharePct}% of hourly tokens` : null, eff.caps.orgMonthlyUsd != null ? `org ≤ $${eff.caps.orgMonthlyUsd}/mo` : null].filter(Boolean).join(' · ') || 'none' : 'none'],
              ]} />
              <p className="cs-help" style={{ marginTop: 10 }}>
                <code>nventr/auto</code> starts on the tier its route allows and moves up only on a verified failure; <code>nventr/image</code> and <code>nventr/video</code> walk their ladders the same way. Name a concrete <code>provider/model</code> to bypass routing. Same rules from the console, the Dev Console gateway, hosted instances and the MCP server.
              </p>
            </>
          )}
        </ModuleFrame.Panel>

        <ModuleFrame.Panel title="Your keys" meta="bring your own — calls on them never draw credits">
          {Object.keys(data.models?.providers || {}).length > 0 ? (
            <table className="mf-table"><tbody>
              {Object.entries(data.models.providers).map(([name, def]) => (
                <tr key={name}>
                  <td style={mono}>{name}</td>
                  <td className="mf-muted" style={mono}>{def.base}</td>
                  <td>{data.keysPresent?.[name] ? <Chip tone="ok">key set</Chip> : <Chip tone="warn">no key</Chip>}</td>
                  <td><button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" disabled={!!busy} onClick={() => dropKey(name, def.keyName)}>Remove</button></td>
                </tr>
              ))}
            </tbody></table>
          ) : <p className="cs-help">No keys of your own yet — everything routes on nventr&rsquo;s keys and credits.</p>}
          <div style={{ display: 'grid', gap: 6, marginTop: 10, maxWidth: 520 }}>
            <input className="cs-input" placeholder="provider name (openai, anthropic, my-vllm…)" value={keyForm.name} onChange={(e) => setKeyForm({ ...keyForm, name: e.target.value })} />
            <input className="cs-input" placeholder="endpoint base URL — leave empty to key a built-in provider" value={keyForm.base} onChange={(e) => setKeyForm({ ...keyForm, base: e.target.value })} />
            <div style={{ display: 'flex', gap: 6 }}>
              <select className="cs-input" style={{ width: 130 }} value={keyForm.wire} onChange={(e) => setKeyForm({ ...keyForm, wire: e.target.value })} aria-label="Wire protocol"><option value="openai">openai wire</option><option value="anthropic">anthropic wire</option></select>
              <input className="cs-input" type="password" placeholder="API key (stored in SSM, never shown again)" value={keyForm.value} onChange={(e) => setKeyForm({ ...keyForm, value: e.target.value })} autoComplete="off" />
              <button type="button" className="cs-btn cs-btn-sm cs-btn-primary" disabled={busy === 'key' || !keyForm.name.trim()} onClick={addKey}>{busy === 'key' ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </ModuleFrame.Panel>
      </ModuleFrame.Columns>

      <ModuleFrame.Panel title="Catalog" meta={`${(data.catalog || []).length} models reachable through the gateway · prices are what the ledger charges`}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
          <input className="cs-input" style={{ maxWidth: 320 }} placeholder="filter — openrouter/anthropic, flux, veo…" value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Filter catalog" />
          <select className="cs-input" style={{ width: 150 }} value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} aria-label="Model kind">
            <option value="all">all kinds</option><option value="chat">chat</option><option value="embed">embeddings</option><option value="image">image</option><option value="video">video</option>
          </select>
        </div>
        <div className="u-scroll-x" style={{ maxHeight: 420, overflowY: 'auto' }}>
          <table className="mf-table">
            <thead><tr><th>Model</th><th>Kind</th><th>Price</th><th>Context</th><th>Source</th></tr></thead>
            <tbody>
              {catalog.map((c) => (
                <tr key={c.id}><td style={mono}>{c.id}</td><td className="mf-muted">{c.kind}</td><td className="mf-muted u-tabular">{priceOf(c)}</td><td className="mf-muted">{c.context ? fmtTokens(c.context) : ''}</td><td className="mf-muted">{c.source}</td></tr>
              ))}
              {!catalog.length && <tr><td colSpan={5} className="mf-muted">No models match.</td></tr>}
            </tbody>
          </table>
        </div>
        <p className="cs-help" style={{ marginTop: 8 }}>Use any row as <code>model</code> on the gateway (<code>/v1/chat/completions</code>, <code>/v1/embeddings</code>, <code>/v1/images/generations</code>, <code>/v1/videos</code>) with a Dev Console gateway key, in a pipeline model block, or in a ladder above. The catalog refreshes nightly from the providers.</p>
      </ModuleFrame.Panel>
    </>
  );
}
